// federalHolidays.js — deterministic US federal holidays + "buy ahead" context.
//
// Pure, no network. The Calendar auto-includes these so the host always sees
// them, and Smart Capture / new dates get a proactive heads-up when they land
// on or just before a holiday (stores closed / holiday hours / crowds).
//
// Observed-day rule (federal): a holiday on Saturday is observed the Friday
// before; on Sunday, the Monday after. We surface BOTH the actual date and the
// observed date so warnings catch either.

const MONTH = { JAN: 0, FEB: 1, MAY: 4, JUN: 5, JUL: 6, SEP: 8, OCT: 9, NOV: 10, DEC: 11 };

function pad2(n) { return String(n).padStart(2, '0'); }
function iso(y, m, d) { return `${y}-${pad2(m + 1)}-${pad2(d)}`; }

// nth weekday (1-indexed) of a month; weekday 0=Sun..6=Sat. nth=-1 => last.
function nthWeekday(year, month, weekday, nth) {
  if (nth === -1) {
    const last = new Date(year, month + 1, 0);
    const offset = (last.getDay() - weekday + 7) % 7;
    return new Date(year, month + 1, 0 - offset);
  }
  const first = new Date(year, month, 1);
  const offset = (weekday - first.getDay() + 7) % 7;
  return new Date(year, month, 1 + offset + (nth - 1) * 7);
}

// Federal observed date for a fixed-date holiday.
function observed(year, month, day) {
  const d = new Date(year, month, day);
  const dow = d.getDay();
  if (dow === 6) return new Date(year, month, day - 1); // Sat -> Fri
  if (dow === 0) return new Date(year, month, day + 1); // Sun -> Mon
  return d;
}

/**
 * federalHolidays(year) -> [{ date, observedDate, name }]
 * `date` is the statutory date; `observedDate` is when offices/stores treat it.
 */
export function federalHolidays(year) {
  const fixed = [
    [MONTH.JAN, 1, "New Year's Day"],
    [MONTH.JUN, 19, 'Juneteenth'],
    [MONTH.JUL, 4, 'Independence Day'],
    [MONTH.NOV, 11, 'Veterans Day'],
    [MONTH.DEC, 25, 'Christmas Day'],
  ].map(([m, d, name]) => {
    const obs = observed(year, m, d);
    return { date: iso(year, m, d), observedDate: iso(obs.getFullYear(), obs.getMonth(), obs.getDate()), name };
  });

  const floating = [
    [nthWeekday(year, MONTH.JAN, 1, 3), 'Martin Luther King Jr. Day'],
    [nthWeekday(year, MONTH.FEB, 1, 3), "Presidents' Day"],
    [nthWeekday(year, MONTH.MAY, 1, -1), 'Memorial Day'],
    [nthWeekday(year, MONTH.SEP, 1, 1), 'Labor Day'],
    [nthWeekday(year, MONTH.OCT, 1, 2), 'Columbus Day'],
    [nthWeekday(year, MONTH.NOV, 4, 4), 'Thanksgiving Day'],
  ].map(([d, name]) => {
    const s = iso(d.getFullYear(), d.getMonth(), d.getDate());
    return { date: s, observedDate: s, name };
  });

  return [...fixed, ...floating].sort((a, b) => a.date.localeCompare(b.date));
}

// All holidays whose date OR observed date falls within [startIso, endIso].
export function holidaysInRange(startIso, endIso) {
  if (!startIso || !endIso) return [];
  const years = new Set();
  years.add(Number(startIso.slice(0, 4)));
  years.add(Number(endIso.slice(0, 4)));
  const out = [];
  years.forEach((y) => federalHolidays(y).forEach((h) => {
    if ((h.date >= startIso && h.date <= endIso) || (h.observedDate >= startIso && h.observedDate <= endIso)) {
      out.push(h);
    }
  }));
  return out.sort((a, b) => a.date.localeCompare(b.date));
}

function daysBetweenIso(a, b) {
  const da = new Date(`${a}T00:00:00`);
  const db = new Date(`${b}T00:00:00`);
  return Math.round((db - da) / 86400000);
}

/**
 * holidayContext(dateIso, { lookaheadDays = 3 }) -> warning object or null.
 * Fires when the date is ON a holiday (closed) or within `lookaheadDays` BEFORE
 * one (crowds / early closures / buy ahead). Returns:
 *   { name, holidayDate, onHoliday, daysBefore, message }
 */
export function holidayContext(dateIso, { lookaheadDays = 3 } = {}) {
  if (!dateIso) return null;
  const d = String(dateIso).slice(0, 10);
  const year = Number(d.slice(0, 4));
  const candidates = [...federalHolidays(year - 1), ...federalHolidays(year), ...federalHolidays(year + 1)];

  let best = null;
  candidates.forEach((h) => {
    // measure against both statutory and observed dates; keep the closest hit
    [h.date, h.observedDate].forEach((hd) => {
      const delta = daysBetweenIso(d, hd); // >0 means holiday is AFTER our date
      const onHoliday = delta === 0;
      const before = delta > 0 && delta <= lookaheadDays;
      if (!onHoliday && !before) return;
      const score = Math.abs(delta);
      if (!best || score < best.score) {
        best = { name: h.name, holidayDate: hd, onHoliday, daysBefore: onHoliday ? 0 : delta, score };
      }
    });
  });
  if (!best) return null;

  const message = best.onHoliday
    ? `That’s ${best.name} — many stores, banks and vendors are closed or on holiday hours. Plan around it.`
    : `Heads up — ${best.name} is ${best.daysBefore} day${best.daysBefore === 1 ? '' : 's'} later. Stores get busy and may close early around then, so buy anything you need ahead of time.`;

  return { name: best.name, holidayDate: best.holidayDate, onHoliday: best.onHoliday, daysBefore: best.daysBefore, message };
}

export default federalHolidays;
